import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../../../layouts/AppLayout/AppLayout';
import { NioSection, NioButton, NioIcon } from '../../../components';
import { useTranslation } from '../../../i18n/I18nProvider';

function RequestDemandStep6() {
  const navigate = useNavigate();
  const location = useLocation();
  const { serviceId } = useParams();
  const { t } = useTranslation();

  const projectProgressOptions = useMemo(
    () => [
      { id: 'plan-and-execution', icon: 'help-circle', label: t('wizard.step6PlanAndExecution') },
      { id: 'execution-only', icon: 'check', label: t('wizard.step6ExecutionOnly') },
    ],
    [t],
  );

  const selectedSubService = location.state?.selectedSubService || t('common.subService');
  const parentServiceTitle = location.state?.parentServiceTitle || t('common.service');
  const projectTitle = location.state?.projectTitle || '';
  const requestDetails = location.state?.requestDetails || {};
  const requestStep4Details = location.state?.requestStep4Details || {};
  const requestStep5Details = location.state?.requestStep5Details || {};
  const isStep5Skipped = location.state?.isStep5Skipped || false;
  const displayStep = isStep5Skipped ? 5 : 6;

  const [projectProgress, setProjectProgress] = useState('');

  return (
    <AppLayout title={`${t('wizard.step', { n: displayStep })} - ${t('wizard.step6Title')}`} rootClass="layout-1">
      <style>{`
        .request-step6-page {
          background: #f2f3f5;
          min-height: 78vh;
          border-radius: 14px;
          position: relative;
          display: flex;
          flex-direction: column;
        }
        .request-step6-content {
          flex: 1 1 auto;
          width: 100%;
          max-width: 760px;
          margin: 0 auto;
          padding-top: 4.2rem;
          padding-bottom: 2rem;
        }
        .request-step6-step {
          color: #697586;
          font-size: 0.9rem;
          margin-bottom: 0.25rem;
        }
        .request-step6-heading {
          color: #0f2b3d;
          font-weight: 700;
          margin-bottom: 0.4rem;
        }
        .request-step6-description {
          color: #5f6b7a;
          margin-bottom: 3rem;
          max-width: 560px;
        }
        .request-step6-question {
          color: #0f2b3d;
          font-size: 2rem;
          font-weight: 500;
          margin-bottom: 1rem;
        }
        .request-step6-options {
          display: grid;
          gap: 0.85rem;
        }
        .request-step6-option {
          border: 1px solid #d6dde5;
          border-radius: 8px;
          background: #fff;
          min-height: 58px;
          display: flex;
          align-items: center;
          gap: 0.9rem;
          padding: 0.65rem 1rem;
          text-align: left;
          width: 100%;
        }
        .request-step6-option.is-active {
          border-color: #2563eb;
          box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.12);
        }
        .request-step6-option-icon {
          color: #101727;
          width: 28px;
          display: flex;
          justify-content: center;
          flex-shrink: 0;
        }
        .request-step6-option-label {
          color: #0f2b3d;
          font-weight: 500;
          line-height: 1.35;
        }
        .request-step6-footer {
          flex-shrink: 0;
          margin-top: auto;
          border-top: 1px solid #dde3ea;
          padding: 0.7rem 1.1rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #f8f9fb;
          border-bottom-left-radius: 14px;
          border-bottom-right-radius: 14px;
          z-index: 5;
          box-shadow: 0 -4px 16px rgba(15, 43, 61, 0.08);
        }
      `}</style>

      <NioSection className="pt-120 pt-lg-160 pb-4">
        <NioSection.Content>
          <div className="request-step6-page p-3 p-md-4">
            <div className="request-step6-content">
              <div className="request-step6-step">{t('wizard.step', { n: displayStep })}</div>
              <h2 className="request-step6-heading">{t('wizard.step6Heading')}</h2>
              <p className="request-step6-description">
                {t('wizard.step6Description')}
              </p>

              <h3 className="request-step6-question">{t('wizard.step6Question')}</h3>
              <div className="request-step6-options">
                {projectProgressOptions.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    className={`request-step6-option ${projectProgress === option.id ? 'is-active' : ''}`}
                    onClick={() => setProjectProgress(option.id)}
                  >
                    <span className="request-step6-option-icon">
                      <NioIcon name={option.icon} className="fs-20" />
                    </span>
                    <span className="request-step6-option-label">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="request-step6-footer">
              <NioButton
                className="btn btn-primary"
                label={t('common.back')}
                onClick={() => navigate(-1)}
              />
              <NioButton
                className={`btn ${projectProgress ? 'btn-primary' : 'btn-primary disabled'}`}
                label={t('common.next')}
                onClick={() => {
                  if (!projectProgress) return;
                  navigate(`/service/${serviceId}/request-demand-step7`, {
                    state: {
                      selectedSubService,
                      parentServiceTitle,
                      projectTitle,
                      isStep5Skipped,
                      requestDetails,
                      requestStep4Details,
                      requestStep5Details,
                      requestStep6Details: {
                        projectProgress,
                      },
                    },
                  });
                }}
              />
            </div>
          </div>
        </NioSection.Content>
      </NioSection>
    </AppLayout>
  );
}

export default RequestDemandStep6;
